
import React, { useState } from "react";
import { Shop } from "@/components/dashboard/shops/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Pencil, MessageSquare, ExternalLink, Copy } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

interface ThankYouPageSectionProps {
  shop: Shop;
}

export const ThankYouPageSection: React.FC<ThankYouPageSectionProps> = ({ shop }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [thankYouMessage, setThankYouMessage] = useState(shop.thank_you_message || "");
  const [thankYouUrl, setThankYouUrl] = useState(shop.thank_you_url || "");
  
  const handleSave = () => {
    // Here, you would implement the logic to save the changes
    // to the Supabase database
    
    // For now, we'll simply display a success toast
    toast.success("Modifications enregistrées avec succès", {
      description: "Les paramètres de la page de remerciement ont été mis à jour."
    });
    
    setIsEditing(false);
  };

  const handleCopyUrl = () => {
    if (thankYouUrl) {
      navigator.clipboard.writeText(thankYouUrl);
      toast.success("URL copiée dans le presse-papier");
    }
  };
  
  return (
    <div className="md:col-span-2">
      <Card className="shadow-md border-0 dark:border dark:border-gray-800">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle>Page De Remerciement</CardTitle>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center gap-1"
          >
            <Pencil className="h-4 w-4" />
            {isEditing ? "Annuler" : "Modifier"}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="thank-you-message" className="flex items-center gap-2">
                <MessageSquare size={16} />
                Message personnalisé de la page de remerciement
              </Label>
              {isEditing ? (
                <Textarea 
                  id="thank-you-message" 
                  value={thankYouMessage} 
                  onChange={(e) => setThankYouMessage(e.target.value)} 
                  placeholder="Message de remerciement après l'achat"
                  rows={4}
                />
              ) : (
                <div className="text-gray-600 dark:text-gray-400 p-3 bg-gray-100 dark:bg-gray-800 rounded-md min-h-20">
                  {thankYouMessage || "Aucun message de remerciement personnalisé défini"}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="thank-you-url" className="flex items-center gap-2">
                <ExternalLink size={16} />
                URL De La Page De Remerciement
              </Label>
              {isEditing ? (
                <Input 
                  id="thank-you-url" 
                  value={thankYouUrl} 
                  onChange={(e) => setThankYouUrl(e.target.value)} 
                  placeholder="https://votre-domaine.com/merci"
                />
              ) : (
                <div className="text-gray-600 dark:text-gray-400 flex items-center">
                  {thankYouUrl ? (
                    <div className="flex items-center justify-between w-full">
                      <a 
                        href={thankYouUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline"
                      >
                        {thankYouUrl}
                      </a>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleCopyUrl}
                        className="ml-2"
                      >
                        <Copy size={14} />
                      </Button>
                    </div>
                  ) : (
                    "Aucune URL de page de remerciement définie"
                  )}
                </div>
              )}
              
              {isEditing && (
                <p className="text-sm text-gray-500 mt-2">
                  Les clients seront redirigés vers cette URL après avoir complété leur achat.
                </p>
              )}
            </div>
            
            {isEditing && (
              <div className="flex justify-end pt-4">
                <Button onClick={handleSave}>Enregistrer les modifications</Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
